import { ForwardedRef, forwardRef, useImperativeHandle, useRef } from 'react';

import { Flex } from '@revenuemarket/cds';
import { colors, theme } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { Typography } from '@revenuemarket/ui';
import { ICellRendererComp, ICellRendererParams } from 'ag-grid-community';
import { Select } from 'antd';

import { LabeledBankAccountTransaction } from '~apis/cash-flow-overview/transactions/getTransactions';
import TransactionMappingModal from '~components/Mapping/TransactionMappingModal';
import useBusinessEntityListQuery from '~hooks/queries/useBusinessEntityListQuery';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { getSplittedText } from '~utils/transaction/getSplittedText';

interface Props extends ICellRendererParams<LabeledBankAccountTransaction> {
  agGridRowData: LabeledBankAccountTransaction[];
}

const AGGridBusinessEntityRenderer = (
  { data, value, agGridRowData }: Props,
  ref: ForwardedRef<ICellRendererComp>,
) => {
  const { companyId } = useDemoAwareCompanyId();
  const { data: businessEntityData } = useBusinessEntityListQuery({
    companyId: companyId as string,
    favoriteOnly: false,
  });

  const businessEntityList = businessEntityData?.Data?.data ?? [];
  const labelCount =
    agGridRowData?.find((row) => row.transactionId === data?.transactionId)?.labelCount ?? 0;
  const isSplitted = labelCount > 1;
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      getGui: () => containerRef.current ?? document.body,
      refresh: () => true,
    }),
    [],
  );

  if (!data) {
    return null;
  }

  if (isSplitted) {
    return (
      <Flex
        align="center"
        style={{ height: '100%', paddingLeft: '8px' }}
        onClick={(e) => {
          e.stopPropagation();
          // TODO: 분할 거래내역 시에만 click 이벤트
          overlays.open(({ isOpen, onClose }) => (
            <TransactionMappingModal
              open={isOpen}
              onCancel={onClose}
              transactionId={data.transactionId}
              showTaxInvoice={false}
            />
          ));
        }}
      >
        <Typography variant="caption1" color={colors.grayscale400} useEllipsis>
          {getSplittedText({
            category: data.businessEntityLabel,
            labelCount: data?.businessEntityLabelCount ?? 0,
            allCount: labelCount,
          })}
        </Typography>
      </Flex>
    );
  }

  const businessEntityLabel = businessEntityList.find((b) => b.id === value)?.name ?? '';

  return (
    <Flex ref={containerRef} align="center" style={{ height: '100%', cursor: 'pointer' }}>
      <Select
        value={businessEntityLabel}
        options={[]}
        open={false}
        bordered={false}
        style={{
          width: '100%',
        }}
        className={css`
          &.ant-select .ant-select-selector .ant-select-selection-item {
            font-size: ${theme.typography.body1.size}px;
          }
        `}
      />
    </Flex>
  );
};

export default forwardRef(AGGridBusinessEntityRenderer);
